import axios from "axios";
import { Item } from './model/item';

export const pageSize = 20;
export const maxPageSize = 100;

export type ItemSortField =
  | 'updatedAt'
  | 'createdAt'
  | 'price'
  | 'costPrice'
  | 'dateOfPurchase'
  | 'soldAt'
  | 'idInt'
  | 'reference';

export type ItemSortDirection = 'asc' | 'desc';

export interface ItemSortOption {
  field: ItemSortField;
  direction: ItemSortDirection;
}

export interface GetItemsParams {
  offset?: number;
  limit?: number;
  q?: string;
  category?: Array<number | string> | number | string;
  sold?: boolean;
  priceMin?: number;
  priceMax?: number;
  soldFrom?: string;
  soldTo?: string;
  reference?: string;
  sort?: ItemSortOption | ItemSortOption[] | string;
}

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const sanitizeString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const serializeCategory = (category: GetItemsParams['category']): string | undefined => {
  if (!category) return undefined;

  if (typeof category === 'number') {
    return String(category);
  }

  if (Array.isArray(category)) {
    const entries = category
      .map((entry) => String(entry).trim())
      .filter((entry) => entry.length > 0);
    if (entries.length === 0) return undefined;
    return entries.join(',');
  }

  return sanitizeString(category);
};

const serializeSort = (sort: GetItemsParams['sort']): string | undefined => {
  if (!sort) return undefined;
  if (typeof sort === 'string') return sanitizeString(sort);

  const options = Array.isArray(sort) ? sort : [sort];
  const serialized = options
    .map((option) => `${option.field}:${option.direction}`)
    .join(',');

  return serialized.length > 0 ? serialized : undefined;
};

const buildGetItemsParams = (params: GetItemsParams): Record<string, string | number | boolean> => {
  const limit = isNumber(params.limit)
    ? Math.max(1, Math.min(maxPageSize, Math.floor(params.limit)))
    : pageSize;

  const offset = isNumber(params.offset)
    ? Math.max(0, Math.floor(params.offset))
    : 0;

  const queryParams: Record<string, string | number | boolean> = {
    limit,
    offset,
  };

  const q = sanitizeString(params.q);
  if (q) queryParams.q = q;

  const category = serializeCategory(params.category);
  if (category) queryParams.category = category;

  if (typeof params.sold === 'boolean') queryParams.sold = params.sold;

  if (isNumber(params.priceMin)) queryParams.priceMin = params.priceMin;
  if (isNumber(params.priceMax)) queryParams.priceMax = params.priceMax;

  const soldFrom = sanitizeString(params.soldFrom);
  if (soldFrom) queryParams.soldFrom = soldFrom;

  const soldTo = sanitizeString(params.soldTo);
  if (soldTo) queryParams.soldTo = soldTo;

  const reference = sanitizeString(params.reference);
  if (reference) queryParams.reference = reference;

  const sort = serializeSort(params.sort);
  if (sort) queryParams.sort = sort;

  return queryParams;
};

class ItemService {
  private toItemUpdatePayload(item: Item): Omit<Item, "matchedBook"> {
    const { matchedBook, ...payload } = item;
    return payload;
  }

  createItem(item: Item) {
    return axios.post("items", item);
  }

  updateItem(item: Item): Promise<Item> {
    return axios.put<Item>("items", this.toItemUpdatePayload(item)).then(response => {
      return response.data;
    });
  }

  getItems(params: GetItemsParams = {}): Promise<PaginatedResponse> {
    const queryParams = buildGetItemsParams(params);
    return axios.get<PaginatedResponse>("items", { params: queryParams }).then((response) => {
      const json: PaginatedResponse = response.data;
      return new PaginatedResponse(json.count, json.totalCount, json.offset, json.limit, json.items);
    });
  }

  getItem(id: string): Promise<Item> {
    return axios.get<Item>(`items/${id}`).then((response) => {
      return response.data;
    });
  }

  markItemSold(item: Item): Promise<Item> {
    const soldAt = new Date().toISOString();
    return axios.put<Item>("items", { ...this.toItemUpdatePayload(item), soldAt: soldAt }).then(response => {
      return {
        ...response.data,
        soldAt: response.data?.soldAt ?? soldAt,
      };
    });
  }

  markItemUnsold(item: Item): Promise<Item> {
    return axios.put<Item>("items", { ...this.toItemUpdatePayload(item), soldAt: undefined }).then(response => {
      return {
        ...response.data,
        soldAt: undefined,
      };
    });
  }
}

export interface PaginatedResponse {
  count: number
  totalCount: number
  offset: number
  limit: number
  items: Item[]
}

export class PaginatedResponse implements PaginatedResponse {
  count: number
  totalCount: number
  offset: number
  limit: number
  items: Item[]

  constructor(count: number, totalCount: number, offset: number, limit: number, items: Item[]) {
    this.count = count;
    this.totalCount = totalCount;
    this.offset = offset;
    this.limit = limit;
    this.items = items;
  }

}

export default new ItemService();
